import type { Product, ProductVariant, Modifier, ModifierGroup, CatalogStatus, PaginationMeta } from '../types/models.js'
import { API_BASE } from './client.js'
import { ApiError } from '../types/api.js'

function token(): string | null {
  return localStorage.getItem('mf_access')
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const t = token()
  if (t) headers['Authorization'] = `Bearer ${t}`
  return headers
}

async function toApiError(res: Response, fallback: string): Promise<ApiError> {
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    // non-JSON error response
  }
  const errBody =
    (body as { error?: { code: string; message: string; details?: Record<string, unknown> } } | null)
      ?.error ?? { code: 'UNKNOWN', message: fallback }
  return new ApiError(res.status, errBody)
}

export interface ProductListParams {
  categoryId?: string
  search?: string
  status?: CatalogStatus
  page?: number
  pageSize?: number
}

export interface CategoryCountsItem {
  categoryId: string
  count: number
}

export interface ProductListResponse {
  data: Product[]
  meta: PaginationMeta
}

export interface CreateProductData {
  categoryId: string
  name: string
  description?: string
  composition?: string
  weight?: number
  calories?: number
  protein?: number
  fat?: number
  carbohydrates?: number
  imageUrl?: string
  price: number
  sortOrder?: number
}

export interface UpdateProductData {
  categoryId?: string
  name?: string
  description?: string | null
  composition?: string | null
  weight?: number | null
  calories?: number | null
  protein?: number | null
  fat?: number | null
  carbohydrates?: number | null
  imageUrl?: string | null
  price?: number
  sortOrder?: number
}

export type ProductLifecycleAction = 'suspend' | 'activate' | 'archive'

function productsPath(organizationId: string): string {
  return `${API_BASE}/organizations/${organizationId}/products`
}

export async function getProducts(
  organizationId: string,
  params: ProductListParams = {},
): Promise<ProductListResponse> {
  const query = new URLSearchParams()
  if (params.categoryId) query.set('categoryId', params.categoryId)
  if (params.search) query.set('search', params.search)
  if (params.status) query.set('status', params.status)
  if (params.page) query.set('page', String(params.page))
  if (params.pageSize) query.set('pageSize', String(params.pageSize))
  const qs = query.toString()

  const res = await fetch(`${productsPath(organizationId)}${qs ? `?${qs}` : ''}`, { headers: authHeaders() })
  if (!res.ok) throw await toApiError(res, 'Failed to load products')
  return res.json() as Promise<ProductListResponse>
}

export async function getProductCategoryCounts(
  organizationId: string,
  params: { search?: string; status?: CatalogStatus } = {},
): Promise<CategoryCountsItem[]> {
  const query = new URLSearchParams()
  if (params.search) query.set('search', params.search)
  if (params.status) query.set('status', params.status)
  const qs = query.toString()

  const res = await fetch(
    `${productsPath(organizationId)}/category-counts${qs ? `?${qs}` : ''}`,
    { headers: authHeaders() },
  )
  if (!res.ok) throw await toApiError(res, 'Failed to load category counts')
  const b = await res.json()
  return b.data as CategoryCountsItem[]
}

export async function getProduct(organizationId: string, productId: string): Promise<Product> {
  const res = await fetch(`${productsPath(organizationId)}/${productId}`, { headers: authHeaders() })
  if (!res.ok) throw await toApiError(res, 'Failed to load product')
  const b = await res.json()
  return b.data
}

export async function createProduct(organizationId: string, data: CreateProductData): Promise<Product> {
  const res = await fetch(productsPath(organizationId), {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to create product')
  const b = await res.json()
  return b.data
}

export async function updateProduct(
  organizationId: string,
  productId: string,
  data: UpdateProductData,
): Promise<Product> {
  const res = await fetch(`${productsPath(organizationId)}/${productId}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to update product')
  const b = await res.json()
  if (!b || typeof b !== 'object' || !b.data) {
    throw new ApiError(502, {
      code: 'INVALID_RESPONSE',
      message: 'Product update returned no data',
    })
  }
  return b.data
}

async function lifecycleProduct(
  organizationId: string,
  productId: string,
  action: ProductLifecycleAction,
): Promise<Product> {
  const res = await fetch(`${productsPath(organizationId)}/${productId}/${action}`, {
    method: 'POST',
    headers: authHeaders(),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to change product status')
  const b = await res.json()
  return b.data
}

export function suspendProduct(organizationId: string, productId: string): Promise<Product> {
  return lifecycleProduct(organizationId, productId, 'suspend')
}

export function activateProduct(organizationId: string, productId: string): Promise<Product> {
  return lifecycleProduct(organizationId, productId, 'activate')
}

export function archiveProduct(organizationId: string, productId: string): Promise<Product> {
  return lifecycleProduct(organizationId, productId, 'archive')
}

export interface CreateVariantData {
  name: string
  price: number
  oldPrice?: number
  sku?: string
  sortOrder?: number
  isDefault?: boolean
}

export interface UpdateVariantData {
  name?: string
  price?: number
  oldPrice?: number | null
  sku?: string | null
  sortOrder?: number
  isDefault?: boolean
}

export type VariantLifecycleAction = 'suspend' | 'activate' | 'archive'

function variantsPath(organizationId: string, productId: string): string {
  return `${productsPath(organizationId)}/${productId}/variants`
}

export async function getVariants(organizationId: string, productId: string): Promise<ProductVariant[]> {
  const res = await fetch(variantsPath(organizationId, productId), { headers: authHeaders() })
  if (!res.ok) throw await toApiError(res, 'Failed to load variants')
  const b = await res.json()
  return b.data
}

export async function getVariant(
  organizationId: string,
  productId: string,
  variantId: string,
): Promise<ProductVariant> {
  const res = await fetch(`${variantsPath(organizationId, productId)}/${variantId}`, { headers: authHeaders() })
  if (!res.ok) throw await toApiError(res, 'Failed to load variant')
  const b = await res.json()
  return b.data
}

export async function createVariant(
  organizationId: string,
  productId: string,
  data: CreateVariantData,
): Promise<ProductVariant> {
  const res = await fetch(variantsPath(organizationId, productId), {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to create variant')
  const b = await res.json()
  return b.data
}

export async function updateVariant(
  organizationId: string,
  productId: string,
  variantId: string,
  data: UpdateVariantData,
): Promise<ProductVariant> {
  const res = await fetch(`${variantsPath(organizationId, productId)}/${variantId}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to update variant')
  const b = await res.json()
  return b.data
}

async function lifecycleVariant(
  organizationId: string,
  productId: string,
  variantId: string,
  action: VariantLifecycleAction,
): Promise<ProductVariant> {
  const res = await fetch(`${variantsPath(organizationId, productId)}/${variantId}/${action}`, {
    method: 'POST',
    headers: authHeaders(),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to change variant status')
  const b = await res.json()
  return b.data
}

export function suspendVariant(organizationId: string, productId: string, variantId: string): Promise<ProductVariant> {
  return lifecycleVariant(organizationId, productId, variantId, 'suspend')
}

export function activateVariant(organizationId: string, productId: string, variantId: string): Promise<ProductVariant> {
  return lifecycleVariant(organizationId, productId, variantId, 'activate')
}

export function archiveVariant(organizationId: string, productId: string, variantId: string): Promise<ProductVariant> {
  return lifecycleVariant(organizationId, productId, variantId, 'archive')
}

export interface CreateModifierGroupData {
  name: string
  min?: number
  max?: number | null
  isRequired?: boolean
  sortOrder?: number
}

export interface UpdateModifierGroupData {
  name?: string
  min?: number
  max?: number | null
  isRequired?: boolean
  sortOrder?: number
}

export type ModifierGroupLifecycleAction = 'suspend' | 'activate' | 'archive'

function modifierGroupsPath(organizationId: string, productId: string): string {
  return `${productsPath(organizationId)}/${productId}/modifier-groups`
}

export async function getModifierGroups(organizationId: string, productId: string): Promise<ModifierGroup[]> {
  const res = await fetch(modifierGroupsPath(organizationId, productId), { headers: authHeaders() })
  if (!res.ok) throw await toApiError(res, 'Failed to load modifier groups')
  const b = await res.json()
  return b.data
}

export async function getModifierGroup(
  organizationId: string,
  productId: string,
  groupId: string,
): Promise<ModifierGroup> {
  const res = await fetch(`${modifierGroupsPath(organizationId, productId)}/${groupId}`, { headers: authHeaders() })
  if (!res.ok) throw await toApiError(res, 'Failed to load modifier group')
  const b = await res.json()
  return b.data
}

export async function createModifierGroup(
  organizationId: string,
  productId: string,
  data: CreateModifierGroupData,
): Promise<ModifierGroup> {
  const res = await fetch(modifierGroupsPath(organizationId, productId), {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to create modifier group')
  const b = await res.json()
  return b.data
}

export async function updateModifierGroup(
  organizationId: string,
  productId: string,
  groupId: string,
  data: UpdateModifierGroupData,
): Promise<ModifierGroup> {
  const res = await fetch(`${modifierGroupsPath(organizationId, productId)}/${groupId}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to update modifier group')
  const b = await res.json()
  return b.data
}

async function lifecycleModifierGroup(
  organizationId: string,
  productId: string,
  groupId: string,
  action: ModifierGroupLifecycleAction,
): Promise<ModifierGroup> {
  const res = await fetch(`${modifierGroupsPath(organizationId, productId)}/${groupId}/${action}`, {
    method: 'POST',
    headers: authHeaders(),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to change modifier group status')
  const b = await res.json()
  return b.data
}

export function suspendModifierGroup(organizationId: string, productId: string, groupId: string): Promise<ModifierGroup> {
  return lifecycleModifierGroup(organizationId, productId, groupId, 'suspend')
}

export function activateModifierGroup(organizationId: string, productId: string, groupId: string): Promise<ModifierGroup> {
  return lifecycleModifierGroup(organizationId, productId, groupId, 'activate')
}

export function archiveModifierGroup(organizationId: string, productId: string, groupId: string): Promise<ModifierGroup> {
  return lifecycleModifierGroup(organizationId, productId, groupId, 'archive')
}

export interface CreateModifierData {
  name: string
  price: number
  maxQuantity?: number
  sortOrder?: number
}

export interface UpdateModifierData {
  name?: string
  price?: number
  maxQuantity?: number
  sortOrder?: number
}

export type ModifierLifecycleAction = 'suspend' | 'activate' | 'archive'

function modifiersPath(organizationId: string, productId: string, groupId: string): string {
  return `${modifierGroupsPath(organizationId, productId)}/${groupId}/modifiers`
}

export async function getModifiers(
  organizationId: string,
  productId: string,
  groupId: string,
): Promise<Modifier[]> {
  const res = await fetch(modifiersPath(organizationId, productId, groupId), { headers: authHeaders() })
  if (!res.ok) throw await toApiError(res, 'Failed to load modifiers')
  const b = await res.json()
  return b.data
}

export async function getModifier(
  organizationId: string,
  productId: string,
  groupId: string,
  modifierId: string,
): Promise<Modifier> {
  const res = await fetch(`${modifiersPath(organizationId, productId, groupId)}/${modifierId}`, { headers: authHeaders() })
  if (!res.ok) throw await toApiError(res, 'Failed to load modifier')
  const b = await res.json()
  return b.data
}

export async function createModifier(
  organizationId: string,
  productId: string,
  groupId: string,
  data: CreateModifierData,
): Promise<Modifier> {
  const res = await fetch(modifiersPath(organizationId, productId, groupId), {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to create modifier')
  const b = await res.json()
  return b.data
}

export async function updateModifier(
  organizationId: string,
  productId: string,
  groupId: string,
  modifierId: string,
  data: UpdateModifierData,
): Promise<Modifier> {
  const res = await fetch(`${modifiersPath(organizationId, productId, groupId)}/${modifierId}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to update modifier')
  const b = await res.json()
  return b.data
}

async function lifecycleModifier(
  organizationId: string,
  productId: string,
  groupId: string,
  modifierId: string,
  action: ModifierLifecycleAction,
): Promise<Modifier> {
  const res = await fetch(`${modifiersPath(organizationId, productId, groupId)}/${modifierId}/${action}`, {
    method: 'POST',
    headers: authHeaders(),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to change modifier status')
  const b = await res.json()
  return b.data
}

export function suspendModifier(organizationId: string, productId: string, groupId: string, modifierId: string): Promise<Modifier> {
  return lifecycleModifier(organizationId, productId, groupId, modifierId, 'suspend')
}

export function activateModifier(organizationId: string, productId: string, groupId: string, modifierId: string): Promise<Modifier> {
  return lifecycleModifier(organizationId, productId, groupId, modifierId, 'activate')
}

export function archiveModifier(organizationId: string, productId: string, groupId: string, modifierId: string): Promise<Modifier> {
  return lifecycleModifier(organizationId, productId, groupId, modifierId, 'archive')
}

// ─── Product Gallery API ─────────────────────────────

export interface ProductImageItem {
  id: string
  productId: string
  url: string
  sortOrder: number
  isPrimary: boolean
}

export interface ImageUploadResult {
  uploaded: { url: string; filename: string }[]
  errors: { filename: string; error: string }[]
}

export async function getProductImages(organizationId: string, productId: string): Promise<ProductImageItem[]> {
  const res = await fetch(
    `${productsPath(organizationId)}/${productId}/images`,
    { headers: authHeaders() },
  )
  if (!res.ok) throw await toApiError(res, 'Failed to load images')
  const b = await res.json()
  return b.data as ProductImageItem[]
}

export async function uploadProductImages(
  organizationId: string, productId: string, files: File[],
): Promise<ImageUploadResult> {
  const form = new FormData()
  for (const f of files) form.append('images', f)
  const token = localStorage.getItem('mf_access')
  const res = await fetch(
    `${productsPath(organizationId)}/${productId}/images/upload`,
    {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    },
  )
  if (!res.ok) throw await toApiError(res, 'Failed to upload images')
  const b = await res.json()
  return b.data as ImageUploadResult
}

export async function createProductImage(
  organizationId: string, productId: string, data: { url: string; sortOrder?: number; isPrimary?: boolean },
): Promise<ProductImageItem> {
  const res = await fetch(
    `${productsPath(organizationId)}/${productId}/images`,
    { method: 'POST', headers: authHeaders(), body: JSON.stringify(data) },
  )
  if (!res.ok) throw await toApiError(res, 'Failed to create image')
  const b = await res.json()
  return b.data as ProductImageItem
}

export async function updateProductImage(
  organizationId: string, productId: string, imageId: string,
  data: { sortOrder?: number; isPrimary?: boolean },
): Promise<ProductImageItem> {
  const res = await fetch(
    `${productsPath(organizationId)}/${productId}/images/${imageId}`,
    { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(data) },
  )
  if (!res.ok) throw await toApiError(res, 'Failed to update image')
  const b = await res.json()
  return b.data as ProductImageItem
}

export async function deleteProductImage(
  organizationId: string, productId: string, imageId: string,
): Promise<void> {
  const res = await fetch(
    `${productsPath(organizationId)}/${productId}/images/${imageId}`,
    { method: 'DELETE', headers: authHeaders() },
  )
  if (!res.ok) throw await toApiError(res, 'Failed to delete image')
}
